import { call } from "@/services/api";
import type { Movement } from "@/services/stockLedger";
import checklists from "@/data/kitChecklists.json";

// His "Fiche article". `purchase` is null for anyone who may not see purchase terms — the server
// leaves them out, it is not this screen's job to hide them.
export type ArticleSheet = {
  reference: string; per_kit: number | null; qty: number | null; kits_possible: number | null;
  references: {
    myotaku_sku: string | null; system_name: string | null; supplier: string | null; supplier_sku: string | null;
    supplier_designation: string | null; ean: string | null;
    on_delivery_notes: { reference: string; supplier: string | null; name: string | null; note: string | null }[];
  };
  purchase: null | {
    price: string | null; currency: string | null; price_note: string | null; moq: string | null;
    supplier_rules: string | null; usual_qty: string | null; remark: string | null; to_confirm: boolean;
  };
  purchase_hidden: boolean;
  in_catalogue: boolean;
  catalogue_imported: boolean;
};

export const fetchArticle = (reference: string, warehouseId: number | null) =>
  call<ArticleSheet>(`/api/v1/articles/${encodeURIComponent(reference)}${warehouseId ? `?warehouse_id=${warehouseId}` : ""}`);
export const fetchArticleMovements = (reference: string, warehouseId: number | null) =>
  call<{ movements: Movement[] }>(`/api/v1/stock_movements?article_reference=${encodeURIComponent(reference)}${warehouseId ? `&warehouse_id=${warehouseId}` : ""}`);

// His article names (ITEM_CATALOG, evaluated into kitChecklists.json), by code — every kit's
// articles, its groups' and its linen phases' too — in the reader's language (German and Italian
// added beside his French and English).
export type ArticleItem = { code: string; name: string; nameEn?: string; nameDe?: string; nameIt?: string; qty: number };
type ChecklistKit = { items?: ArticleItem[] | null; groups?: { items: ArticleItem[] }[] | null;
  phases?: { installed: ArticleItem[]; reserve: ArticleItem[] } | null };
const NAMES = new Map<string, ArticleItem>();
for (const k of (checklists as { kits: ChecklistKit[] }).kits) {
  const all = [...(k.items ?? []), ...(k.groups ?? []).flatMap((g) => g.items), ...(k.phases ? [...k.phases.installed, ...k.phases.reserve] : [])];
  for (const i of all) if (!NAMES.has(i.code)) NAMES.set(i.code, i);
}
export const nameIn = (i: ArticleItem, locale: string) =>
  ({ fr: i.name, en: i.nameEn, de: i.nameDe, it: i.nameIt }[locale] || i.nameEn || i.name);
export const articleName = (code: string, locale: string) => {
  const i = NAMES.get(code);
  return i ? nameIn(i, locale) : null;
};
export const articleItem = (code: string) => NAMES.get(code) ?? null;

// His `fmtPrice`: the currency, then the amount with a comma — four decimals when a price has them.
export function formatPrice(v: string | null, currency: string | null) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "";
  const n = Number(v);
  const dec = Math.abs(n - Math.round(n * 100) / 100) > 1e-9 ? 4 : 2;
  return `${currency ? `${currency} ` : ""}${n.toFixed(dec).replace(".", ",")}`;
}
